// src/components/MatchDetails.jsx
import React, { useState, useEffect } from "react";
import "./MatchDetails.css";

import { supabase } from "../supabaseClient";

const FRESH_MS = 60_000; // 1 min cache

function MatchDetails({
  themeColor,
  secondaryColor,
  gameID,
  homeTeam,
  awayTeam,
}) {
  const [playerGoals, setPlayerGoals] = useState({ home: [], away: [] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const KEY = `matchGoals:${gameID}`;
    const cached = (() => {
      try {
        return JSON.parse(sessionStorage.getItem(KEY) || "null");
      } catch {
        return null;
      }
    })();

    const now = Date.now();

    if (cached?.data) {
      setPlayerGoals(cached.data);
      setLoading(false);
    }

    if (cached && now - cached.ts < FRESH_MS) return;

    const fetchPlayerGoals = async () => {
      try {
        const { data: rows, error } = await supabase
          .from('player_goals')
          .select(`
            id, 
            goals, 
            players (
              player_name,
              player_teams (
                teams (team_name)
              )
            )
          `)
          .eq('game_id', gameID);
          
        if (error) throw error;

        const home = [];
        const away = [];

        for (const row of rows || []) {
          const playerName = row.players?.player_name;
          const goalRecord = {
            id: row.id,
            name: playerName,
            goals: row.goals
          };
          
          // Check which team the player belongs to
          let assigned = false;
          const playerTeams = row.players?.player_teams || [];
          
          for (const pt of playerTeams) {
            const tName = pt.teams?.team_name;
            if (tName === homeTeam) {
              home.push(goalRecord);
              assigned = true;
              break;
            } else if (tName === awayTeam) {
              away.push(goalRecord);
              assigned = true;
              break;
            }
          }
          
          // Fallback if not strictly assigned to home or away (should not happen if data is correct)
          if (!assigned) {
            // we could try to guess, but skipping is safer
          }
        }

        if (!cancelled) {
          setPlayerGoals({ home, away });
          setFailed(false);
          sessionStorage.setItem(
            KEY,
            JSON.stringify({ data: { home, away }, ts: Date.now() })
          );
        }
      } catch (error) {
        console.error("Error fetching game goals:", error);
        if (!cancelled && !cached?.data) {
          setPlayerGoals({ home: [], away: [] });
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPlayerGoals();
    return () => {
      cancelled = true; // ignore late responses
    };
  }, [gameID, homeTeam, awayTeam]);

  const { home = [], away = [] } = playerGoals || {};

  return (
    <div
      className="match-details-container"
      style={{
        "--theme-color": themeColor,
        "--secondary-color": secondaryColor,
      }}
    >
      <div className="match-details-label">Goleadores</div>
      <div className="teams-header-row">
        <div className="teams-header-item home">{homeTeam || "Por definir"}</div>
        <div className="teams-header-item away">{awayTeam || "Por definir"}</div>
      </div>
      {loading ? (
        <div className="match-details-status" role="status">
          Cargando goleadores…
        </div>
      ) : failed ? (
        <div className="match-details-status error" role="alert">
          No se pudieron cargar los goleadores.
        </div>
      ) : (
        <PlayerGoals home={home} away={away} />
      )}
    </div>
  );
}

function PlayerGoals({ home = [], away = [] }) {
  return (
    <div className="teams-players-container">
      {/* Home column */}
      <div className="teams-player-column home">
        {home.length === 0 ? (
          <div className="teams-player-item home empty">Sin goles</div>
        ) : (
          home.map((p, i) => (
            <div key={`${p.name}-${i}`} className="teams-player-item home">
              <div className="teams-player-item-name">{p.name}</div>
              <div className="player-goals-container">{p.goals ?? 0}</div>
            </div>
          ))
        )}
      </div>

      {/* Away column */}
      <div className="teams-player-column away">
        {away.length === 0 ? (
          <div className="teams-player-item away empty">Sin goles</div>
        ) : (
          away.map((p, i) => (
            <div key={`${p.name}-${i}`} className="teams-player-item away">
              <div className="teams-player-item-name">{p.name}</div>
              <div className="player-goals-container">{p.goals ?? 0}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default MatchDetails;
